// Compact summary of the built-in defaults that ship with the deployment,
// shown just below the page description rather than as its own section:
//   - The always-allowed domains are described inline (allowlist mode).
//   - The default blocked competitor domains open in a modal on demand
//     (blocklist mode) since they're a longer list few admins need to read.
// Both lists are managed via database seeds and are read-only here.
import { useEffect, useState, Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Chip, Stack, CircularProgress, Alert,
} from '@mui/material';
import { fetchSeededDefaults } from '../../../store/admin-blacklist-slice.js';

// Inline monospace "code" styling so a pattern reads as a domain rule, not a
// literal address.
const codeSx = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  bgcolor: 'action.hover',
  color: 'text.primary',
  px: 0.5,
  py: '1px',
  borderRadius: 0.75,
  fontSize: '0.85em',
  whiteSpace: 'nowrap',
};

// A domain-suffix pattern (e.g. "gov", "sanborn.com") matches that domain and
// any subdomain of it, so we show it wildcard-prefixed ("*.gov"). An exact
// pattern (a specific address an admin added) is shown verbatim.
const formatAllowed = ({ pattern, pattern_type: type }) => (
  type === 'domain' ? `*.${pattern}` : pattern
);

const BuiltInDefaultsNote = () => {
  const dispatch = useDispatch();
  const {
    allowlist, competitors, loading, error,
  } = useSelector((s) => s.adminBlacklist.defaults);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchSeededDefaults());
  }, [dispatch]);

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="body2" color="text.secondary">
        In allowlist mode, addresses on
        {' '}
        {allowlist.length ? allowlist.map((item, i) => (
          <Fragment key={`${item.pattern_type}:${item.pattern}`}>
            {i > 0 ? ', ' : ''}
            <Box component="code" sx={codeSx}>{formatAllowed(item)}</Box>
          </Fragment>
        )) : 'a built-in set of domains'}
        {' '}
        are always allowed in addition to the entries you add below.
      </Typography>
      <Button
        variant="text"
        size="small"
        onClick={() => setOpen(true)}
        sx={{ px: 0, mt: 0.5, textTransform: 'none' }}
      >
        {`View default blocked competitors${competitors.length ? ` (${competitors.length})` : ''}`}
      </Button>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Default blocked competitors</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            These domains ship with the deployment and are blocked automatically
            in blocklist mode. They&apos;re managed via database seeds — add your
            own entries below to extend the list.
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {loading && competitors.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={22} aria-label="Loading competitors" />
            </Box>
          ) : (
            <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
              {competitors.length ? competitors.map((item) => (
                <Chip key={`${item.pattern_type}:${item.pattern}`} label={formatAllowed(item)} variant="outlined" size="small" sx={{ fontFamily: codeSx.fontFamily }} />
              )) : (
                <Typography variant="body2" color="text.secondary">
                  No seeded competitor domains.
                </Typography>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BuiltInDefaultsNote;
